import * as Dialog from '@radix-ui/react-dialog';
import { X, RefreshCw } from 'lucide-react';

import { Pagination } from '@/components/shared/Pagination';
import { Button } from '@/components/ui/button';
import { useWebhookDeliveries } from '@/hooks/useWebhooks';
import { formatDateTime } from '@/utils/format';

import type { Webhook } from '@/lib/api/webhooks.api';

interface DeliveriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  webhook: Webhook | null;
  page: number;
  onPageChange: (page: number) => void;
}

const STATUS_STYLES: Record<string, string> = {
  SUCCESS: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  FAILED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  PENDING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
};

export function DeliveriesDialog({
  open,
  onOpenChange,
  webhook,
  page,
  onPageChange,
}: DeliveriesDialogProps) {
  const { data, isLoading, refetch } = useWebhookDeliveries(
    open ? webhook?.id : undefined,
    page,
    10,
  );

  const deliveries = data?.data ?? [];

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 bg-black/50" />
        <Dialog.Content className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed top-[50%] left-[50%] z-50 max-h-[90vh] w-full max-w-3xl translate-x-[-50%] translate-y-[-50%] overflow-y-auto rounded-lg bg-white p-6 shadow-lg dark:bg-slate-900">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <Dialog.Title className="text-lg font-semibold">Delivery Log</Dialog.Title>
              <Dialog.Description className="text-sm text-gray-600 dark:text-gray-400">
                Deliveries for &ldquo;{webhook?.name}&rdquo;
              </Dialog.Description>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="mr-1 h-3 w-3" />
                Refresh
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="border-primary h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
            </div>
          ) : deliveries.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
              No deliveries yet.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-500 dark:text-gray-400">
                      <th className="pr-4 pb-2 font-medium">Event</th>
                      <th className="pr-4 pb-2 font-medium">Status</th>
                      <th className="pr-4 pb-2 font-medium">HTTP</th>
                      <th className="pr-4 pb-2 font-medium">Attempts</th>
                      <th className="pb-2 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveries.map((delivery) => (
                      <tr
                        key={delivery.id}
                        className="border-b last:border-b-0 dark:border-slate-700"
                      >
                        <td className="py-2 pr-4">
                          <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs dark:bg-slate-800">
                            {delivery.event}
                          </code>
                        </td>
                        <td className="py-2 pr-4">
                          <span
                            className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[delivery.status] ?? ''}`}
                          >
                            {delivery.status}
                          </span>
                        </td>
                        <td className="text-muted-foreground py-2 pr-4 text-xs">
                          {delivery.response_status_code ?? '-'}
                        </td>
                        <td className="text-muted-foreground py-2 pr-4 text-xs">
                          {delivery.attempts}
                        </td>
                        <td className="text-muted-foreground py-2 text-xs">
                          {formatDateTime(delivery.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {data && data.totalPages > 1 && (
                <Pagination
                  page={data.page}
                  totalPages={data.totalPages}
                  total={data.total}
                  limit={data.limit}
                  onPageChange={onPageChange}
                  resourceName="deliveries"
                />
              )}
            </>
          )}

          <Dialog.Close asChild>
            <button
              className="ring-offset-background focus:ring-ring absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
